/**
 * @file date-field-format.ts
 * @description Формат поля даты кабинета (`DateField`, phase19.31): маска
 * «дд.мм.гггг» при вводе с клавиатуры и перевод в значение формы
 * «гггг-мм-дд» — тот же формат, что отдавало нативное `<input type="date">`,
 * поэтому логика форм (сроки годности, периоды) не меняется.
 *
 * Чистые функции без React — проверяются юнит-тестом.
 */

/** «гггг-мм-дд» — формат значения формы. */
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** «дд.мм.гггг» — формат показа. */
const RU_RE = /^(\d{2})\.(\d{2})\.(\d{4})$/;

/** Есть ли такой день в календаре (31.02 — нет). */
function isRealDate(year: number, month: number, day: number): boolean {
  if (year < 1900 || year > 2999 || month < 1 || month > 12 || day < 1) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** «2027-03-15» → «15.03.2027»; пусто и мусор — пустая строка. */
export function isoToDisplay(iso: string): string {
  const m = ISO_RE.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

/**
 * Разбор введённого: «15.03.2027» (или вставленное «2027-03-15») →
 * `{ iso }`; неполный ввод — `{ incomplete: true }`; несуществующий день —
 * `{ invalid: true }`; пусто — `{ iso: "" }`.
 */
export function parseDateInput(text: string): { iso: string } | { incomplete: true } | { invalid: true } {
  const trimmed = text.trim();
  if (!trimmed) return { iso: "" };
  const iso = ISO_RE.exec(trimmed);
  const ru = RU_RE.exec(trimmed);
  const parts = iso ? [iso[1], iso[2], iso[3]] : ru ? [ru[3], ru[2], ru[1]] : null;
  if (!parts) return trimmed.replace(/\D/g, "").length < 8 ? { incomplete: true } : { invalid: true };
  const [y, mo, d] = parts.map(Number) as [number, number, number];
  if (!isRealDate(y, mo, d)) return { invalid: true };
  return { iso: `${parts[0]}-${parts[1]}-${parts[2]}` };
}

/**
 * Маска при наборе: цифры раскладываются в «дд.мм.гггг», точки ставятся сами.
 * Вставленное «гггг-мм-дд» переводится в показ целиком.
 */
export function maskDateInput(text: string): string {
  if (ISO_RE.test(text.trim())) return isoToDisplay(text.trim());
  const digits = text.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

/** Date (локальный день из календаря) → «гггг-мм-дд». */
export function dateToIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** «гггг-мм-дд» → Date (локальная полночь) для календаря; пусто — undefined. */
export function isoToDate(iso: string): Date | undefined {
  const m = ISO_RE.exec(iso);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
